/**
 * WebSocket transport for voice/realtime sessions.
 *
 * Connects to the /realtime/{session_id} endpoint and handles
 * the AgentScope realtime event protocol (JSON, no binary frames).
 */

export type VoiceConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'error';

export interface VoiceTransportConfig {
  /** Base URL of the agent server (e.g., http://localhost:8090) */
  baseUrl: string;
  /** Voice endpoint path (default: '/realtime') */
  voiceEndpoint?: string;
  /** Callback when connection state changes */
  onConnectionStateChange?: (state: VoiceConnectionState) => void;
  /** Callback for incoming events from the server */
  onEvent?: (event: Record<string, unknown>) => void;
}

/**
 * WebSocket transport for voice/realtime communication.
 */
export class VoiceTransport {
  private config: VoiceTransportConfig;
  private ws: WebSocket | null = null;
  private connectionState: VoiceConnectionState = 'disconnected';

  constructor(config: VoiceTransportConfig) {
    this.config = {
      voiceEndpoint: '/realtime',
      ...config,
    };
  }

  /**
   * Connect to the voice WebSocket endpoint.
   *
   * Browsers cannot set custom headers on WebSocket upgrades, so the
   * JWT is forwarded as the ``session_token`` query param — same name
   * the orchestrator's text-mode WS uses. ``organization_id`` is
   * optional; the backend can also extract it from the JWT.
   */
  async connect(
    sessionId: string,
    userId: string,
    auth?: { sessionToken?: string; organizationId?: string },
  ): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.setConnectionState('connecting');

    return new Promise((resolve, reject) => {
      const baseUrl = this.config.baseUrl.replace(/^http/, 'ws');
      const url = new URL(
        `${baseUrl}${this.config.voiceEndpoint}/${sessionId}`,
      );
      url.searchParams.set('user_id', userId);
      if (auth?.organizationId) {
        url.searchParams.set('organization_id', auth.organizationId);
      }
      if (auth?.sessionToken) {
        url.searchParams.set('session_token', auth.sessionToken);
      }
      this.ws = new WebSocket(url.toString());

      let pending = true;

      this.ws.onopen = () => {
        pending = false;
        this.setConnectionState('connected');
        resolve();
      };

      this.ws.onclose = () => {
        if (!pending) {
          this.setConnectionState('disconnected');
        }
      };

      this.ws.onerror = () => {
        if (pending) {
          pending = false;
          this.cleanup();
          this.setConnectionState('error');
          reject(new Error('Voice WebSocket connection failed'));
        }
      };

      this.ws.onmessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data) as Record<string, unknown>;
          this.config.onEvent?.(data);
        } catch {
          // Ignore malformed messages
        }
      };
    });
  }

  /**
   * Send a JSON event to the server.
   */
  send(event: Record<string, unknown>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('Voice WebSocket not connected');
      return;
    }
    this.ws.send(JSON.stringify(event));
  }

  /**
   * Send the session.create event to initiate the voice session.
   *
   * @param options Optional voice/language overrides sent to the server.
   */
  sendSessionCreate(options?: {
    voice?: string;
    inputLanguage?: string;
  }): void {
    this.send({
      type: 'session.create',
      ...(options?.voice && { voice: options.voice }),
      ...(options?.inputLanguage && { input_language: options.inputLanguage }),
    });
  }

  /**
   * Send an audio input buffer append event.
   */
  sendAudioInput(base64Audio: string): void {
    this.send({
      type: 'input_audio_buffer.append',
      audio: base64Audio,
    });
  }

  /**
   * Commit the audio input buffer (trigger transcription + response).
   */
  sendAudioCommit(): void {
    this.send({ type: 'input_audio_buffer.commit' });
  }

  /**
   * Cancel the current response.
   */
  sendResponseCancel(): void {
    this.send({ type: 'response.cancel' });
  }

  /**
   * Check if connected.
   */
  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Get the current connection state.
   */
  getConnectionState(): VoiceConnectionState {
    return this.connectionState;
  }

  /**
   * Disconnect and clean up.
   */
  dispose(): void {
    this.cleanup();
    this.setConnectionState('disconnected');
  }

  private cleanup(): void {
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.close();
      this.ws = null;
    }
  }

  private setConnectionState(state: VoiceConnectionState): void {
    this.connectionState = state;
    this.config.onConnectionStateChange?.(state);
  }
}
